import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../../../components/table/TableLegacy";
import { useFulfillmentTypeList } from "../../../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { Button, Icon } from "@sellar-io/components";
import { useDeliveryMethodContainerRulesCreate } from "../../../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesCreate";
import { DeliveryMethodContainerRuleUnitType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { capitalize } from "@sellar-io/common";

export default function ItemDeliveryMethodRulesDialogContent({
  onDismiss,
  onBack,
  onSubmit,
  containerType,
  volume,
  displayUnit,
}: {
  onDismiss: () => void;
  onBack: () => void;
  onSubmit: () => void;
  containerType: string;
  volume: number;
  displayUnit: string;
}) {
  const [excludedDeliveryMethodIds, setExcludedDeliveryMethodIds] = useState<
    Set<number>
  >(new Set());

  const fulfillmentTypeListQuery = useFulfillmentTypeList({
    limit: 50,
  });

  const deliveryMethods =
    fulfillmentTypeListQuery.data?.pages
      .flat()
      .filter((method) => method.fulfillmentType !== "sellarDistribution") ??
    [];

  const deliveryMethodContainerRulesCreateMutation =
    useDeliveryMethodContainerRulesCreate();

  const toggleDeliveryMethod = (id: number) => {
    const newExcluded = new Set(excludedDeliveryMethodIds);
    if (newExcluded.has(id)) {
      newExcluded.delete(id);
    } else {
      newExcluded.add(id);
    }
    setExcludedDeliveryMethodIds(newExcluded);
  };

  const handleSubmit = async () => {
    const promises: Promise<{ id: number }>[] = [];

    excludedDeliveryMethodIds.forEach((deliveryMethodId) => {
      promises.push(
        deliveryMethodContainerRulesCreateMutation.mutateAsync({
          containerType,
          unit: displayUnit as DeliveryMethodContainerRuleUnitType,
          volume,
          FulfillmentTypeId: deliveryMethodId,
          SupplierId: null,
          systemDefinedType: null,
        }),
      );
    });

    if (promises.length > 0) {
      await Promise.all(promises);
    }

    onSubmit();
  };

  return (
    <div>
      <DialogContent>
        <DialogNavigation
          title="Delivery method availability"
          description={`Select which delivery methods ${volume}${displayUnit} ${containerType} should be available for.`}
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => onDismiss()}
        />

        <div className={"flex flex-col gap-4"}>
          <div className="flex flex-col gap-2">
            <TableLegacyContainer className={"max-h-75"}>
              <TableLegacy>
                <TableLegacyTHead>
                  <TableLegacyTR>
                    <TableLegacyTH className={"sticky top-0"}>
                      Delivery method
                    </TableLegacyTH>
                    <TableLegacyTH className={"sticky top-0"}>
                      Available
                    </TableLegacyTH>
                  </TableLegacyTR>
                </TableLegacyTHead>
                <TableLegacyTBody>
                  {deliveryMethods.map((deliveryMethod) => (
                    <TableLegacyTR key={deliveryMethod.id}>
                      <TableLegacyTD>
                        {deliveryMethod.fulfillmentType === "sellarDistribution"
                          ? "Sellar Distribution"
                          : deliveryMethod.name
                            ? `${deliveryMethod.name} - ${capitalize(deliveryMethod.fulfillmentType)}`
                            : capitalize(deliveryMethod.fulfillmentType)}
                      </TableLegacyTD>
                      <TableLegacyTD>
                        <input
                          type="checkbox"
                          checked={
                            !excludedDeliveryMethodIds.has(deliveryMethod.id)
                          }
                          onChange={() =>
                            toggleDeliveryMethod(deliveryMethod.id)
                          }
                        />
                      </TableLegacyTD>
                    </TableLegacyTR>
                  ))}
                </TableLegacyTBody>
              </TableLegacy>
            </TableLegacyContainer>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className="flex items-center justify-between">
        <Button color="tertiary" type="button" onClick={onBack}>
          <div className="flex">
            <Icon icon="CHEVRON_LEFT" />
            <div>Back</div>
          </div>
        </Button>
        <Button
          loading={deliveryMethodContainerRulesCreateMutation.isPending}
          onClick={handleSubmit}
        >
          Confirm
        </Button>
      </DialogCTA>
    </div>
  );
}
